/**
 * reporting-live.ts — Live Supabase queries for the Reporting Hub and its
 * drill-down pages. Wired against the real schema:
 *   tenants, users, accounts, account_cp_status, account_answers,
 *   hunt_items, decision_makers, sales_plans, v_ae_pipeline_summary,
 *   v_account_forecast.
 *
 * Tenant scoping: SessionUser does not carry tenant_id, so every query
 * pins .eq("tenant_id", DEFAULT_TENANT_ID). Partner tenant is tenant 1 today.
 *
 * No mock fallback. If the DB is empty, callers render the empty state.
 */
import { supabase, getSession, restoreSession } from "./session";
import { HEALTH_PALETTE } from "@shared/schema";

export const DEFAULT_TENANT_ID = 2; // Mi6 Demo tenant

/**
 * Returns the current session's tenant_id, falling back to DEFAULT_TENANT_ID.
 * Uses the in-memory session when available; never blocks on async from callers.
 */
function getActiveTenantId(): number {
  const s = getSession();
  return s?.tenantId ?? DEFAULT_TENANT_ID;
}

/**
 * Async variant of getActiveTenantId. If no session is cached in memory it
 * attempts a restoreSession() call (reads from Supabase Auth + users table)
 * so that queries fired before the app bootstrap completes still get the
 * correct tenant_id. Falls back to DEFAULT_TENANT_ID if the restore fails
 * or returns nothing.
 *
 * Returns Promise<number> — the active tenant ID for the logged-in user.
 */
async function getActiveTenantIdAsync(): Promise<number> {
  let s = getSession();
  if (!s) {
    try {
      s = await restoreSession();
    } catch {
      // restoreSession failed — fall back to default
    }
  }
  return s?.tenantId ?? DEFAULT_TENANT_ID;
}

// ── CP labels ──────────────────────────────────────────────────────────────
// Canonical CP names locked 2026-05-26 by Bryan K. Short.
export const CP_LABELS: Record<number, string> = {
  1: "Project",
  2: "Need / Pain / Demonstration",
  3: "Budget",
  4: "Timeline",
  5: "Authority — Project / Initiative",
  6: "Technical and Financial Sign-off",
  7: "Org Structure and Influence Map",
  8: "Competitive Position",
  9: "Compliance — IT Internal and Industry-Specific",
  10: "Legal — Contracts, SLAs, Terms",
};

// W64 canon 5-bucket taxonomy. No Probable. Won is a deal status, not a bucket.
export type Bucket = "Commit" | "Upside" | "Best Case" | "Pipeline" | "Omit" | "WON" | "LOST";

/** Canon bucket constants — W64 locked 2026-05-30 */
export const FORECAST_BUCKETS = [
  { id: 'Commit',     floor: 80, color: '#16A34A' },
  { id: 'Upside',     floor: 65, color: '#16A34A' },
  { id: 'Best Case',  floor: 50, color: '#EAB308' },
  { id: 'Pipeline',   floor: 30, color: '#EA580C' },
  { id: 'Omit',       floor:  0, color: '#DC2626' },
] as const;

// ── Row types ──────────────────────────────────────────────────────────────
export interface UserRow {
  id: number;
  email: string;
  full_name: string;
  name?: string; // alias populated from full_name
  role: string;
  tenant_id: number;
}

export interface AccountRow {
  id: number;
  tenant_id: number;
  owner_user_id: number;
  name: string;
  industry: string | null;
  est_arr: number | null; // derived from aip_ext->>'est_arr' at query time
  stage: string | null;
  bucket: string | null;
  score: number | null;
  updated_at: string;
}

export interface CpStatusRow {
  account_id: number;
  cp_number: number;
  status: string | null;
  note: string;
  is_manual: boolean;
  updated_at: string;
}

export interface AnswerRow {
  account_id: number;
  question_key: string;
  selected_option: string | null;
  evidence: string;
  is_assumption: boolean;
  updated_at: string;
}

export interface HuntItemRow {
  id: number;
  account_id: number;
  item_key: string | null;
  label: string | null;
  status: string;
  owner: string;
  evidence: string;
  linked_cp: number | null;
  due_date: string | null;
  industry_trigger: string | null;
  note?: string;
  updated_at: string;
  source?: string | null;
  outcome?: string | null;
}

export interface DecisionMakerRow {
  id: number;
  account_id: number;
  name: string;
  title: string;
  // DB column is relationship_strength; strength is derived for compatibility
  relationship_strength: number | null;
  strength: number; // alias: relationship_strength ?? 1
}

export interface SalesPlanRow {
  id: number;
  tenant_id: number;
  ae_user_id: number;
  plan_year: number; // column is plan_year in schema (not fiscal_year)
  fiscal_year?: number; // alias kept for downstream consumers
  annual_quota: number; // actual DB column name
  quota_annual: number; // alias for downstream consumers that reference quota_annual
}

export interface PipelineSummaryRow {
  ae_user_id: number;
  tenant_id: number;
  full_name: string;
  email: string;
  role: string;
  quota_annual: number | null;
  active_opps: number;
  commit_value: number;
  /** @deprecated W64: Probable removed from canon. Use upside_value or best_case_value. */
  probable_value: number;
  upside_value: number;
  best_case_value: number;
  pipeline_value: number;
  omitted_value: number;
  won_value: number;
  weighted_pipeline: number;
}

// W64: forecast_weight and weighted_value removed. forecast_color added.
export interface ForecastRow {
  account_id: number;
  tenant_id: number;
  ae_user_id: number;
  account_name: string;
  status: string | null;
  composite_score: number | null;
  deal_value: number;
  forecast_bucket: string;
  forecast_color: string;
  /** @deprecated W64: removed from canon. No weighted percentages. */
  forecast_weight?: number;
  /** @deprecated W64: removed from canon. No weighted percentages. */
  weighted_value?: number;
}

// ── Helpers ────────────────────────────────────────────────────────────────
// Canon W64 bucket colors — 5 score-driven buckets. No Probable. No weighted percentages.
// WON/LOST are deal statuses, not forecast buckets.
export function bucketColor(b?: string | null): string {
  const norm = (b || "").toLowerCase().replace(/_/g, " ");
  switch (norm) {
    case "commit":
      return "#16A34A";
    case "upside":
      return "#16A34A";
    case "best case":
      return "#EAB308";
    case "pipeline":
      return "#EA580C";
    case "omit":
    case "omitted":
      return "#DC2626";
    case "won":
      return "#0B0B0F";
    case "lost":
      return "#8E8E94";
    default:
      return "#8E8E94";
  }
}

export function fmtUSD(n: number | null | undefined): string {
  const v = Number(n || 0);
  if (v >= 1_000_000) return `$${(v / 1_000_000).toFixed(1)}M`;
  if (v >= 1_000) return `$${Math.round(v / 1_000)}K`;
  return `$${v.toFixed(0)}`;
}

export function daysSince(iso: string | null | undefined): number {
  if (!iso) return Infinity;
  const t = new Date(iso).getTime();
  if (isNaN(t)) return Infinity;
  return Math.floor((Date.now() - t) / 86_400_000);
}

export function pctOf(n: number, d: number): number {
  if (!d) return 0;
  return Math.round((n / d) * 100);
}

// ── Queries ────────────────────────────────────────────────────────────────

/** Normalise a raw users row to the UserRow interface. */
function normaliseUser(r: any): UserRow {
  const displayName = r.full_name ?? r.name ?? r.email?.split("@")[0] ?? "User";
  return { ...r, full_name: displayName, name: displayName };
}

export async function listAEs(): Promise<UserRow[]> {
  const tenantId = await getActiveTenantIdAsync(); // Bug-C fix: async restore before querying
  const { data, error } = await supabase()
    .from("users")
    .select("id,email,full_name,role,tenant_id")
    .eq("tenant_id", tenantId)
    .eq("role", "ae")
    .order("full_name");
  if (error) throw error;
  return (data || []).map(normaliseUser) as UserRow[];
}

export async function listAllUsers(): Promise<UserRow[]> {
  const tenantId = await getActiveTenantIdAsync(); // Bug-C fix: async restore before querying
  const { data, error } = await supabase()
    .from("users")
    .select("id,email,full_name,role,tenant_id")
    .eq("tenant_id", tenantId)
    .order("full_name");
  if (error) throw error;
  return (data || []).map(normaliseUser) as UserRow[];
}

/**
 * Normalise a raw accounts row to the AccountRow interface.
 * The DB schema stores:
 *   - stage data in the `status` column (not `stage`)
 *   - bucket, score, est_arr inside the `aip_ext` JSONB column
 * We map them here so all downstream consumers use the stable AccountRow shape.
 */
function normaliseAccount(r: any): AccountRow {
  const ext = r.aip_ext ?? {};
  const estArr = r.est_arr ?? (ext.est_arr != null ? Number(ext.est_arr) : 0);
  return {
    ...r,
    // `status` is the actual DB column; expose it as `stage` for compatibility
    stage: r.status ?? r.stage ?? null,
    // bucket and score live in aip_ext, not as top-level columns
    bucket: r.bucket ?? ext.bucket ?? null,
    score: r.score ?? ext.score ?? null,
    est_arr: estArr,
  };
}

/** Shared accounts select — uses actual DB column names. */
const ACCOUNTS_SELECT =
  "id,tenant_id,owner_user_id,name,industry,status,aip_ext,updated_at";

export async function listAccounts(): Promise<AccountRow[]> {
  const tenantId = await getActiveTenantIdAsync(); // Bug-C fix
  const { data, error } = await supabase()
    .from("accounts")
    .select(ACCOUNTS_SELECT)
    .eq("tenant_id", tenantId)
    .order("updated_at", { ascending: false });
  if (error) throw error;
  return (data || []).map(normaliseAccount) as AccountRow[];
}

export async function getAccount(accountId: number): Promise<AccountRow | null> {
  const tenantId = await getActiveTenantIdAsync(); // Bug-C fix
  const { data, error } = await supabase()
    .from("accounts")
    .select(ACCOUNTS_SELECT)
    .eq("tenant_id", tenantId)
    .eq("id", accountId)
    .maybeSingle();
  if (error) throw error;
  return data ? normaliseAccount(data) : null;
}

export async function listCpStatusFor(accountIds: number[]): Promise<CpStatusRow[]> {
  if (accountIds.length === 0) return [];
  const { data, error } = await supabase()
    .from("account_cp_status")
    .select("account_id,cp_number,status,note,is_manual,updated_at")
    .in("account_id", accountIds);
  if (error) throw error;
  return (data || []) as CpStatusRow[];
}

export async function listAnswersFor(accountId: number): Promise<AnswerRow[]> {
  const { data, error } = await supabase()
    .from("account_answers")
    .select("account_id,question_key,selected_option,evidence,is_assumption,updated_at")
    .eq("account_id", accountId)
    .order("updated_at", { ascending: false });
  if (error) throw error;
  return (data || []) as AnswerRow[];
}

export async function listHuntFor(accountId: number, limit = 50): Promise<HuntItemRow[]> {
  const { data, error } = await supabase()
    .from("hunt_items")
    .select("*")
    .eq("account_id", accountId)
    .order("updated_at", { ascending: false })
    .limit(limit);
  if (error) throw error;
  return (data || []) as HuntItemRow[];
}

export async function listAllHunt(): Promise<HuntItemRow[]> {
  const { data, error } = await supabase()
    .from("hunt_items")
    .select("*")
    .order("updated_at", { ascending: false });
  if (error) throw error;
  return (data || []) as HuntItemRow[];
}

/** Normalise a raw decision_makers row — DB has relationship_strength, not strength. */
function normaliseDm(r: any): DecisionMakerRow {
  const rs = r.relationship_strength ?? r.strength ?? null;
  return { ...r, relationship_strength: rs, strength: Number(rs ?? 1) };
}

export async function listDecisionMakersFor(accountId: number): Promise<DecisionMakerRow[]> {
  // Use select("*") to tolerate schema evolution; normaliseDm maps columns correctly.
  const { data, error } = await supabase()
    .from("decision_makers")
    .select("*")
    .eq("account_id", accountId)
    .order("name");
  if (error) throw error;
  return (data || []).map(normaliseDm) as DecisionMakerRow[];
}

export async function listAllDecisionMakers(): Promise<DecisionMakerRow[]> {
  const { data, error } = await supabase()
    .from("decision_makers")
    .select("*");
  if (error) throw error;
  return (data || []).map(normaliseDm) as DecisionMakerRow[];
}

export async function listSalesPlans(): Promise<SalesPlanRow[]> {
  const tenantId = await getActiveTenantIdAsync(); // Bug-C fix
  // DB column is annual_quota (not quota_annual). Select it and alias both names
  // so all downstream consumers work regardless of which name they reference.
  const { data, error } = await supabase()
    .from("sales_plans")
    .select("id,tenant_id,ae_user_id,plan_year,annual_quota")
    .eq("tenant_id", tenantId);
  if (error) throw error;
  // Populate fiscal_year alias (plan_year is the real column) and
  // quota_annual alias (annual_quota is the real column) for downstream consumers.
  return (data || []).map((r: any) => ({
    ...r,
    fiscal_year: r.plan_year,
    quota_annual: Number(r.annual_quota ?? 0),
  })) as SalesPlanRow[];
}

export async function listPipelineSummary(): Promise<PipelineSummaryRow[]> {
  const tenantId = await getActiveTenantIdAsync(); // Bug-C fix
  const { data, error } = await supabase()
    .from("v_ae_pipeline_summary")
    .select("*")
    .eq("tenant_id", tenantId);
  if (error) throw error;
  return (data || []) as PipelineSummaryRow[];
}

export async function listAccountForecasts(): Promise<ForecastRow[]> {
  const tenantId = await getActiveTenantIdAsync(); // Bug-C fix
  const { data, error } = await supabase()
    .from("v_account_forecast")
    .select("*")
    .eq("tenant_id", tenantId);
  if (error) throw error;
  return (data || []) as ForecastRow[];
}

// ── Derived helpers ────────────────────────────────────────────────────────

/**
 * For each account, count CP rows that are "green" (cleared). Returns a Map
 * keyed by account_id with { green, scored, lastUpdated, byCp }.
 */
export function summarizeCpStatus(rows: CpStatusRow[]): Map<
  number,
  { green: number; scored: number; lastUpdated: string | null; byCp: Record<number, CpStatusRow> }
> {
  const out = new Map<
    number,
    { green: number; scored: number; lastUpdated: string | null; byCp: Record<number, CpStatusRow> }
  >();
  for (const r of rows) {
    let entry = out.get(r.account_id);
    if (!entry) {
      entry = { green: 0, scored: 0, lastUpdated: null, byCp: {} };
      out.set(r.account_id, entry);
    }
    entry.byCp[r.cp_number] = r;
    if (r.status) entry.scored += 1;
    if (r.status === "green") entry.green += 1;
    if (!entry.lastUpdated || r.updated_at > entry.lastUpdated) entry.lastUpdated = r.updated_at;
  }
  return out;
}
